import { List } from "@mui/material";
import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AttachmentLinkEnum, DocumentResponse } from "@akitabox/api-client";

import { api } from "../../api";
import { EmptyItem } from "./EmptyItem";
import { LoadingItem } from "./LoadingItem";
import { FileSection } from "./FileSection";
import { stylesheet } from "../../stylesheet";
import { makeUseServiceCall } from "../../hooks/useServiceCall";
import { useGlobalSnackbar } from "../../consecutive-snackbar/GlobalSnackbar";
import { AttachmentDialogTestids } from "../AttachmentDialog";
import { ActiveFilters, FilterBar, FilterKind } from "./FilterBar";

export interface FileListSectionProps {
  orgId: string;
  building: string;
  className?: string;
  onFileSelection: (file: DocumentResponse) => void;
  selectedFiles: Record<DocumentResponse["_id"], DocumentResponse>;
  entityId: string;
  entityType: AttachmentLinkEnum;
}

/**
 * Conditionally renders the list of files in a org and building (optional)
 * @param `FileListSectionProps`
 * @returns FunctionComponent
 */
export const FileListSection: FunctionComponent<FileListSectionProps> = ({
  orgId,
  building,
  entityId,
  entityType,
  className,
  selectedFiles,
  onFileSelection,
}) => {
  const { simple } = useGlobalSnackbar();
  const [filters, setFilters] = useState<ActiveFilters>({
    [FilterKind.Name]: "",
    [FilterKind.Extension]: "",
  });

  const {
    error,
    isLoading,
    data: response,
  } = makeUseServiceCall(api.documents.getByOrganization)({
    orgId,
    building,
    archived: false,
    limit: 100,
    name: filters[FilterKind.Name] || undefined,
    extension: filters[FilterKind.Extension] || undefined,
  });

  const { isLoading: attachmentsLoading, data: attachmentsResponse } =
    makeUseServiceCall(api.attachments.getByBuilding)({
      building,
      entityId,
      entityType,
    });

  useEffect(() => {
    if (!error) return;

    let message = "Unknown network error";
    // making sure error message is not ""
    if (error.message) {
      message = error.message;
    }

    simple(message, {
      severity: "error",
      testId: AttachmentDialogTestids.FileListError,
    });
  }, [error, simple]);

  const files = useMemo(() => {
    if (response && attachmentsResponse) {
      return response.data.filter(
        (document) =>
          !attachmentsResponse.data.some(
            (attachment) => attachment.document._id === document._id
          )
      );
    }
  }, [response, attachmentsResponse]);

  let content: ReactNode = <EmptyItem css={ss.centered} />;

  if (isLoading || attachmentsLoading) {
    content = <LoadingItem css={ss.centered} />;
  } else if (files && files.length > 0) {
    content = files.map((file) => (
      <FileSection
        file={file}
        key={file._id}
        onFileSelection={onFileSelection}
        checked={Boolean(selectedFiles[file._id])}
      />
    ));
  }

  const handleFilterChange = (filters: ActiveFilters) => {
    setFilters(filters);
  };

  return (
    <div css={ss.fileListWrapper} className={className}>
      <FilterBar
        onFilterChange={handleFilterChange}
        activeFilters={filters}
      ></FilterBar>
      <List>{content}</List>
    </div>
  );
};

const ss = stylesheet({
  centered: { justifyContent: "center" },
  fileListWrapper: {
    width: "100%",
    overflowY: "auto",
  },
});
